import React, { useState, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { testimonials } from "../mock";
import useReveal from "../hooks/useReveal";

function getPerView(width) {
  if (width < 640) return 1;
  if (width < 1024) return 2;
  return 3;
}

export default function Testimonials() {
  const [ref, inView] = useReveal();
  const [perView, setPerView] = useState(
    typeof window !== "undefined" ? getPerView(window.innerWidth) : 3
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const onResize = () => setPerView(getPerView(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - perView);

  // Clamp index when perView changes
  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const prev = useCallback(
    () => setIndex((i) => (i <= 0 ? maxIndex : i - 1)),
    [maxIndex]
  );
  const next = useCallback(
    () => setIndex((i) => (i >= maxIndex ? 0 : i + 1)),
    [maxIndex]
  );

  return (
    <section className="bg-[#EAE9F6] py-6">
      <div
        ref={ref}
        className={`reveal ${inView ? "in-view" : ""} mx-auto max-w-7xl px-5 md:px-8`}
      >
        <h2 className="text-center font-serif-display text-3xl font-bold text-[#6618D0] md:text-[2.3rem]">
          What Parents &amp; Educators Are Saying
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-center text-[15px] leading-relaxed text-[#4a4a4a]">
          Parents and educators in Canada and internationally have used
          Rachelle's approach to respond more calmly, hold clearer boundaries,
          strengthen connection, and better understand the needs behind
          children's behaviour.
        </p>

        {/* Carousel */}
        <div className="relative mt-10">
          {/* Prev arrow */}
          <button
            onClick={prev}
            aria-label="Previous testimonials"
            className="absolute -left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#6618D0] shadow-md ring-1 ring-[#D8CFF0] transition-all hover:bg-[#6618D0] hover:text-white active:scale-95 md:-left-5"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Track */}
          <div className="overflow-hidden px-1">
            <div
              className="flex items-stretch transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${index * (100 / perView)}%)` }}
            >
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="shrink-0 px-3"
                  style={{ width: `${100 / perView}%` }}
                >
                  <div className="flex h-full flex-col rounded-xl bg-white/70 p-7 text-center shadow-sm ring-1 ring-[#D8CFF0] transition-transform hover:-translate-y-1">
                    <div className="flex justify-center gap-1">
                      {[...Array(5)].map((_, s) => (
                        <Star
                          key={s}
                          className="h-4 w-4 fill-[#48B6F7] text-[#48B6F7]"
                        />
                      ))}
                    </div>
                    <p className="mt-4 flex-1 text-[14px] leading-relaxed text-[#3a3a3a]">
                      {t.text}
                    </p>
                    <p className="mt-4 font-serif-display text-[13px] font-bold text-[#6618D0]">
                      — {t.author}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next arrow */}
          <button
            onClick={next}
            aria-label="Next testimonials"
            className="absolute -right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#6618D0] shadow-md ring-1 ring-[#D8CFF0] transition-all hover:bg-[#6618D0] hover:text-white active:scale-95 md:-right-5"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Dots */}
        <div className="mt-7 flex justify-center gap-2">
          {[...Array(maxIndex + 1)].map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2.5 rounded-full transition-all ${
                index === i
                  ? "w-6 bg-[#6618D0]"
                  : "w-2.5 bg-[#C8B2F7] hover:bg-[#9A6CE3]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
